import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type PlatformSupportTicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved';

export interface PlatformSupportTicketSummary {
  id: string;
  tenant_protocol: string;
  status: PlatformSupportTicketStatus;
  priority: 'normal' | 'high';
  summary: string;
  customer_note: string | null;
  origin_path: string | null;
  unit_label: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  tenant_billing_state: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformSupportTicketDetail extends PlatformSupportTicketSummary {
  tenant_conversation_id: string | null;
  requester: Record<string, unknown>;
  dossier: Record<string, unknown>;
  tenant_api_base_url: string | null;
  internal_note: string | null;
  resolved_at: string | null;
  linked_conversation_id?: string | null;
  linked_channel_id?: string | null;
  linked_contact_phone?: string | null;
}

export interface PlatformSupportWhatsappInbox {
  id: string;
  name: string;
  path: string;
  provider: string;
  channelId?: string;
  instanceId?: string;
  type: string;
  inboxSlug: string;
}

interface ListResponse {
  success: boolean;
  data?: { tickets: PlatformSupportTicketSummary[]; total: number };
  error?: string;
}

interface OneResponse {
  success: boolean;
  data?: PlatformSupportTicketDetail;
  error?: string;
}

export async function listPlatformSupportTickets(params: {
  status?: PlatformSupportTicketStatus;
  tenant?: string;
  limit?: number;
  offset?: number;
}): Promise<{ tickets: PlatformSupportTicketSummary[]; total: number }> {
  const res = await axios.get<ListResponse>(getApiUrl('/platform-admin/support/tickets'), { params });
  if (!res.data?.success) throw new Error(res.data?.error || 'Falha ao carregar inbox.');
  return res.data.data || { tickets: [], total: 0 };
}

export async function getPlatformSupportTicket(id: string): Promise<PlatformSupportTicketDetail> {
  const res = await axios.get<OneResponse>(getApiUrl(`/platform-admin/support/tickets/${encodeURIComponent(id)}`));
  if (!res.data?.success || !res.data.data) throw new Error(res.data?.error || 'Chamado não encontrado.');
  return res.data.data;
}

export interface PlatformSupportTicketStats {
  open: number;
  in_progress: number;
  waiting_customer: number;
  total_active: number;
}

export async function getPlatformSupportTicketStats(): Promise<PlatformSupportTicketStats> {
  const res = await axios.get<{ success: boolean; data?: PlatformSupportTicketStats; error?: string }>(
    getApiUrl('/platform-admin/support/tickets/stats'),
  );
  if (!res.data?.success) {
    throw new Error(res.data?.error || 'Não foi possível carregar contadores de chamados.');
  }
  if (!res.data.data) {
    return { open: 0, in_progress: 0, waiting_customer: 0, total_active: 0 };
  }
  return res.data.data;
}

export async function listPlatformSupportWhatsappInboxes(): Promise<PlatformSupportWhatsappInbox[]> {
  const res = await axios.get<{ success: boolean; data?: PlatformSupportWhatsappInbox[]; error?: string }>(
    getApiUrl('/platform-admin/support/inboxes'),
  );
  if (!res.data?.success) throw new Error(res.data?.error || 'Falha ao listar inboxes de suporte.');
  return res.data.data || [];
}

export async function syncPlatformSupportTicketsInbox(): Promise<{
  synced: number;
  errors: string[];
  skipped?: string | null;
}> {
  const res = await axios.post<{
    success: boolean;
    data?: { synced: number; errors: string[]; skipped?: string | null };
    error?: string;
  }>(getApiUrl('/platform-admin/support/tickets/sync-inbox'));
  if (!res.data?.success) throw new Error(res.data?.error || 'Falha ao sincronizar chamados com o inbox.');
  return res.data.data || { synced: 0, errors: [], skipped: null };
}

export async function getPlatformSupportTicketByConversation(conversationId: string): Promise<PlatformSupportTicketDetail> {
  const res = await axios.get<OneResponse>(
    getApiUrl(`/platform-admin/support/tickets/by-conversation/${encodeURIComponent(conversationId)}`),
  );
  if (!res.data?.success || !res.data.data) throw new Error(res.data?.error || 'Chamado não encontrado.');
  return res.data.data;
}

export async function resolvePlatformSupportTicketForConversation(conversationId: string): Promise<void> {
  await axios.post(getApiUrl(`/platform-admin/support/tickets/by-conversation/${encodeURIComponent(conversationId)}/resolve`));
}

export async function patchPlatformSupportTicket(
  id: string,
  body: {
    status?: PlatformSupportTicketStatus;
    assigned_to?: string | null;
    internal_note?: string | null;
    priority?: 'normal' | 'high';
    linked_conversation_id?: string | null;
    linked_channel_id?: string | null;
    linked_contact_phone?: string | null;
  },
): Promise<PlatformSupportTicketDetail> {
  const res = await axios.patch<OneResponse>(getApiUrl(`/platform-admin/support/tickets/${encodeURIComponent(id)}`), body);
  if (!res.data?.success || !res.data.data) throw new Error(res.data?.error || 'Não foi possível atualizar.');
  return res.data.data;
}

export const SUPPORT_TICKET_STATUS_LABEL: Record<PlatformSupportTicketStatus, string> = {
  open: 'Novo',
  in_progress: 'Em atendimento',
  waiting_customer: 'Aguardando cliente',
  resolved: 'Resolvido',
};
